import React, { useEffect, useRef, useState } from 'react';
import { Close } from "@mui/icons-material";
import api from '../../services/api';

interface PescadorFormProps {
  pescadorUid?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const PescadorForm: React.FC<PescadorFormProps> = ({ pescadorUid, onClose, onSaved }) => {
  const isEdicao = !!pescadorUid;

  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [telefone, setTelefone] = useState('');
  const [registroPescador, setRegistroPescador] = useState('');
  const [uidColonia, setUidColonia] = useState('');

  // Endereço
  const [cep, setCep] = useState('');
  const [logradouro, setLogradouro] = useState('');
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');
  const [bairro, setBairro] = useState('');
  const [idEstado, setIdEstado] = useState('');
  const [idMunicipio, setIdMunicipio] = useState<number | ''>('');
  const [municipioTexto, setMunicipioTexto] = useState('');
  const [sugestoesMunicipio, setSugestoesMunicipio] = useState<any[]>([]);
  const [buscandoMunicipio, setBuscandoMunicipio] = useState(false);
  const municipioDebounce = useRef<any>(null);

  const [colonias, setColonias] = useState<any[]>([]);
  const [projetos, setProjetos] = useState<any[]>([]);
  const [areas, setAreas] = useState<any[]>([]);
  const [projetosSelecionados, setProjetosSelecionados] = useState<string[]>([]);
  const [areasSelecionadas, setAreasSelecionadas] = useState<string[]>([]);
  const [carregando, setCarregando] = useState(isEdicao);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get('/pesca/colonias').then(res => setColonias(res.data.data));
    api.get('/pesca/projetos').then(res => setProjetos(res.data.data));
    api.get('/pesca/areas').then(res => setAreas(res.data.data));
  }, []);

  useEffect(() => {
    if (!pescadorUid) return;
    api.get(`/pesca/pescadores/${pescadorUid}`).then(res => {
      const p = res.data.data;
      setNome(p.nome || '');
      setCpf(p.cpf || '');
      setTelefone(p.telefone || '');
      setRegistroPescador(p.registro_pescador || '');
      setUidColonia(p.uid_pesca_colonia || '');
      setCep(p.cep || '');
      setLogradouro(p.logradouro || '');
      setNumero(p.numero || '');
      setComplemento(p.complemento || '');
      setBairro(p.bairro || '');
      setIdEstado(p.id_estado || '');
      setIdMunicipio(p.id_municipio || '');
      setMunicipioTexto(p.municipio?.municipio || '');
      setProjetosSelecionados(
        (p.projetoVinculos || []).map((v: any) => v.projeto?.uid_pesca_projeto).filter(Boolean)
      );
      setAreasSelecionadas(
        (p.areaVinculos || []).map((v: any) => v.area?.uid_pesca_area).filter(Boolean)
      );
      setCarregando(false);
    });
  }, [pescadorUid]);

  const toggle = (lista: string[], setLista: (v: string[]) => void, uid: string) => {
    setLista(lista.includes(uid) ? lista.filter(x => x !== uid) : [...lista, uid]);
  };

  const handleBuscarMunicipio = (texto: string) => {
    setMunicipioTexto(texto);
    setIdMunicipio(''); // limpa seleção anterior até escolher de novo
    if (municipioDebounce.current) clearTimeout(municipioDebounce.current);

    if (texto.trim().length < 2) {
      setSugestoesMunicipio([]);
      return;
    }

    municipioDebounce.current = setTimeout(async () => {
      setBuscandoMunicipio(true);
      const params = new URLSearchParams({ search: texto.trim() });
      if (idEstado) params.set('id_estado', idEstado);
      const res = await api.get(`/pesca/municipios?${params.toString()}`);
      setSugestoesMunicipio(res.data.data);
      setBuscandoMunicipio(false);
    }, 400);
  };

  const selecionarMunicipio = (m: any) => {
    setIdMunicipio(m.id_municipio);
    setMunicipioTexto(m.municipio);
    if (m.id_estado) setIdEstado(m.id_estado);
    setSugestoesMunicipio([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) {
      setErro('Informe o nome do pescador.');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      const payload = {
        nome: nome.trim(),
        cpf: cpf.trim() || null,
        telefone: telefone.trim() || null,
        registro_pescador: registroPescador.trim() || null,
        uid_pesca_colonia: uidColonia || null,
        cep: cep.trim() || null,
        logradouro: logradouro.trim() || null,
        numero: numero.trim() || null,
        complemento: complemento.trim() || null,
        bairro: bairro.trim() || null,
        id_estado: idEstado || null,
        id_municipio: idMunicipio || null,
        uid_pesca_projetos: projetosSelecionados,
        uid_pesca_areas: areasSelecionadas,
      };

      if (isEdicao) {
        await api.put(`/pesca/pescadores/${pescadorUid}`, payload);
      } else {
        await api.post('/pesca/pescadores', payload);
      }
      onSaved();
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Erro ao salvar pescador.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, color: 'var(--green-700, #00b37e)' }}>
            {isEdicao ? 'Editar Pescador' : 'Novo Pescador'}
          </h2>
          <Close style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>

        {carregando ? (
          <p>Carregando...</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={fieldWrapStyle}>
              <label style={labelStyle}>Nome *</label>
              <input style={inputStyle} value={nome} onChange={(e) => setNome(e.target.value)} />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ ...fieldWrapStyle, flex: 1 }}>
                <label style={labelStyle}>CPF</label>
                <input style={inputStyle} value={cpf} onChange={(e) => setCpf(e.target.value)} />
              </div>
              <div style={{ ...fieldWrapStyle, flex: 1 }}>
                <label style={labelStyle}>Telefone</label>
                <input style={inputStyle} value={telefone} onChange={(e) => setTelefone(e.target.value)} />
              </div>
            </div>

            <div style={fieldWrapStyle}>
              <label style={labelStyle}>Registro de Pescador</label>
              <input style={inputStyle} value={registroPescador} onChange={(e) => setRegistroPescador(e.target.value)} />
            </div>

            <div style={fieldWrapStyle}>
              <label style={labelStyle}>Colônia</label>
              <select style={inputStyle} value={uidColonia} onChange={(e) => setUidColonia(e.target.value)}>
                <option value="">Não informada</option>
                {colonias.map((c) => (
                  <option key={c.uid_pesca_colonia} value={c.uid_pesca_colonia}>{c.nome}</option>
                ))}
              </select>
            </div>

            {/* ── Endereço ─────────────────────────────────────────────── */}
            <div style={{ ...fieldWrapStyle, borderTop: '1px solid #eee', paddingTop: '12px', marginTop: '4px' }}>
              <label style={{ ...labelStyle, fontWeight: 'bold', color: '#444' }}>Endereço</label>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ ...fieldWrapStyle, width: '140px' }}>
                <label style={labelStyle}>CEP</label>
                <input style={inputStyle} value={cep} onChange={(e) => setCep(e.target.value)} placeholder="00000-000" />
              </div>
              <div style={{ ...fieldWrapStyle, width: '80px' }}>
                <label style={labelStyle}>UF</label>
                <select style={inputStyle} value={idEstado} onChange={(e) => setIdEstado(e.target.value)}>
                  <option value="">-</option>
                  {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              </div>
              <div style={{ ...fieldWrapStyle, flex: 1, position: 'relative' }}>
                <label style={labelStyle}>Município</label>
                <input
                  style={inputStyle}
                  value={municipioTexto}
                  onChange={(e) => handleBuscarMunicipio(e.target.value)}
                  placeholder="Digite pra buscar..."
                  autoComplete="off"
                />
                {(buscandoMunicipio || sugestoesMunicipio.length > 0) && (
                  <div style={suggestionBoxStyle}>
                    {buscandoMunicipio && <div style={{ padding: '8px', color: '#999', fontSize: '0.85rem' }}>Buscando...</div>}
                    {!buscandoMunicipio && sugestoesMunicipio.map((m) => (
                      <div
                        key={m.id_municipio}
                        style={suggestionItemStyle}
                        onClick={() => selecionarMunicipio(m)}
                      >
                        {m.municipio} {m.id_estado ? `- ${m.id_estado}` : ''}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ ...fieldWrapStyle, flex: 2 }}>
                <label style={labelStyle}>Logradouro</label>
                <input style={inputStyle} value={logradouro} onChange={(e) => setLogradouro(e.target.value)} />
              </div>
              <div style={{ ...fieldWrapStyle, width: '100px' }}>
                <label style={labelStyle}>Número</label>
                <input style={inputStyle} value={numero} onChange={(e) => setNumero(e.target.value)} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ ...fieldWrapStyle, flex: 1 }}>
                <label style={labelStyle}>Bairro</label>
                <input style={inputStyle} value={bairro} onChange={(e) => setBairro(e.target.value)} />
              </div>
              <div style={{ ...fieldWrapStyle, flex: 1 }}>
                <label style={labelStyle}>Complemento</label>
                <input style={inputStyle} value={complemento} onChange={(e) => setComplemento(e.target.value)} />
              </div>
            </div>

            <div style={fieldWrapStyle}>
              <label style={labelStyle}>Projeto(s)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                {projetos.map((p) => (
                  <label key={p.uid_pesca_projeto} style={chipStyle(projetosSelecionados.includes(p.uid_pesca_projeto))}>
                    <input
                      type="checkbox"
                      style={{ marginRight: '6px' }}
                      checked={projetosSelecionados.includes(p.uid_pesca_projeto)}
                      onChange={() => toggle(projetosSelecionados, setProjetosSelecionados, p.uid_pesca_projeto)}
                    />
                    {p.nome}
                  </label>
                ))}
                {projetos.length === 0 && <span style={{ color: '#999', fontSize: '0.85rem' }}>Nenhum projeto cadastrado.</span>}
              </div>
            </div>

            <div style={fieldWrapStyle}>
              <label style={labelStyle}>Área(s) de Pesca</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                {areas.map((a) => (
                  <label key={a.uid_pesca_area} style={chipStyle(areasSelecionadas.includes(a.uid_pesca_area))}>
                    <input
                      type="checkbox"
                      style={{ marginRight: '6px' }}
                      checked={areasSelecionadas.includes(a.uid_pesca_area)}
                      onChange={() => toggle(areasSelecionadas, setAreasSelecionadas, a.uid_pesca_area)}
                    />
                    {a.nome}
                  </label>
                ))}
                {areas.length === 0 && <span style={{ color: '#999', fontSize: '0.85rem' }}>Nenhuma área cadastrada.</span>}
              </div>
            </div>

            {erro && <p style={{ color: '#f75a68', fontSize: '0.85rem' }}>{erro}</p>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button type="button" onClick={onClose} style={secondaryBtnStyle}>Cancelar</button>
              <button type="submit" disabled={salvando} style={primaryBtnStyle}>
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, overflowY: 'auto', padding: '20px 0',
};
const modalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: '12px', padding: '24px',
  width: '90%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
};
const fieldWrapStyle: React.CSSProperties = { marginBottom: '14px' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '4px' };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: 'none', background: '#03c9fe', color: '#fff', cursor: 'pointer',
};
const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer',
};
const chipStyle = (ativo: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', padding: '4px 10px', borderRadius: '16px',
  border: `1px solid ${ativo ? '#03c9fe' : '#ddd'}`, background: ativo ? '#e6f9ff' : '#fff',
  fontSize: '0.85rem', cursor: 'pointer',
});
const suggestionBoxStyle: React.CSSProperties = {
  position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
  border: '1px solid #ddd', borderRadius: '8px', marginTop: '2px', zIndex: 10,
  maxHeight: '160px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
};
const suggestionItemStyle: React.CSSProperties = {
  padding: '8px 10px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f0f0f0',
};

export default PescadorForm;
